/* Model personality — field model. Every field is a column on the account row;
   the page edits them as one draft and autosaves the changed columns. */

import type { Account } from '../../../../api/client';
import { CHAT_STYLE_PRESETS, type ChatStylePreset } from '../../chatStylePresets';

export type Section = 'style' | 'info';

export type TextKey =
  | 'conversational_dynamics' | 'hot_conversational_dynamics'
  | 'base_information' | 'physical_traits' | 'family_information' | 'current_life_context'
  | 'preferences_and_hobbies' | 'emotional_patterns_and_triggers' | 'memories' | 'additional_details'
  | 'sexual_fetishes_and_positioning_in_sex' | 'content_inventory';
export type EmojiKey = 'normal_chat_emojis' | 'hot_chat_emojis';
export type FieldKey = TextKey | EmojiKey;

/** `id` is the i18n key under `mp.f`; `icon` is a Feather icon (one per field, no repeats). */
export interface FieldDef { key: FieldKey; id: string; icon: string; emoji?: boolean; max?: number }

const MAX = 1000;
// Chat-style prompts and the price list get more room than the biography fields.
const LONG = 2000;

const F = {
  ns: { key: 'conversational_dynamics', id: 'ns', icon: 'message-square', max: LONG },
  ne: { key: 'normal_chat_emojis', id: 'ne', icon: 'smile', emoji: true },
  hs: { key: 'hot_conversational_dynamics', id: 'hs', icon: 'thermometer', max: LONG },
  he: { key: 'hot_chat_emojis', id: 'he', icon: 'zap', emoji: true },
  base: { key: 'base_information', id: 'base', icon: 'user', max: MAX },
  phys: { key: 'physical_traits', id: 'phys', icon: 'eye', max: MAX },
  fam: { key: 'family_information', id: 'fam', icon: 'home', max: MAX },
  cur: { key: 'current_life_context', id: 'cur', icon: 'map-pin', max: MAX },
  pref: { key: 'preferences_and_hobbies', id: 'pref', icon: 'music', max: MAX },
  emo: { key: 'emotional_patterns_and_triggers', id: 'emo', icon: 'activity', max: MAX },
  mem: { key: 'memories', id: 'mem', icon: 'book', max: MAX },
  add: { key: 'additional_details', id: 'add', icon: 'edit-3', max: MAX },
  fet: { key: 'sexual_fetishes_and_positioning_in_sex', id: 'fet', icon: 'heart', max: MAX },
  inv: { key: 'content_inventory', id: 'inv', icon: 'tag', max: LONG },
} satisfies Record<string, FieldDef>;

/** Titled groups per section. `tz` puts the timezone row at the top of the group. */
export const GROUPS: Record<Section, { id: string; tz?: boolean; fields: FieldDef[] }[]> = {
  style: [
    { id: 'normal', fields: [F.ns, F.ne] },
    { id: 'hot', fields: [F.hs, F.he] },
  ],
  info: [
    { id: 'about', tz: true, fields: [F.base, F.phys, F.fam, F.cur] },
    { id: 'inner', fields: [F.pref, F.emo, F.mem, F.add] },
    { id: 'adult', fields: [F.fet, F.inv] },
  ],
};

export const sectionFields = (s: Section) => GROUPS[s].flatMap((g) => g.fields);
const ALL = [...sectionFields('style'), ...sectionFields('info')];

/** Curated IANA timezones for the model's local clock. */
export const TIMEZONES: { id: string; label: string }[] = [
  { id: 'Europe/Kyiv', label: 'Kyiv (Europe/Kyiv)' },
  { id: 'Europe/London', label: 'London (Europe/London)' },
  { id: 'Europe/Warsaw', label: 'Warsaw (Europe/Warsaw)' },
  { id: 'Europe/Berlin', label: 'Berlin (Europe/Berlin)' },
  { id: 'Europe/Istanbul', label: 'Istanbul (Europe/Istanbul)' },
  { id: 'America/New_York', label: 'US East (America/New_York)' },
  { id: 'America/Chicago', label: 'US Central (America/Chicago)' },
  { id: 'America/Denver', label: 'US Mountain (America/Denver)' },
  { id: 'America/Los_Angeles', label: 'US Pacific (America/Los_Angeles)' },
  { id: 'UTC', label: 'UTC' },
];
export const DEFAULT_TIMEZONE = 'Europe/Kyiv';

export const QUICK_EMOJI = ['🥰', '😘', '😊', '😍', '😂', '🥹', '❤️', '💕', '💞', '😉', '🙈', '🔥', '😏', '😈', '💋', '💦', '🥵', '👅', '🍑', '💄'];

/** Stored as a comma-separated string; edited as a list. Split on commas only so
 *  free-text entries like ":)" or "<3" survive the round-trip. */
export const parseEmojis = (raw: string | null | undefined) => (raw ? raw.split(',').map((e) => e.trim()).filter(Boolean) : []);
const serializeEmojis = (a: string[]) => (a.length ? a.join(', ') : null);

/** The emoji graphemes in a typed/pasted string ("😂👍🏽" → ["😂", "👍🏽"]). */
export function splitEmojis(s: string): string[] {
  // Intl.Segmenter keeps multi-codepoint emojis (skin tones, ZWJ sequences) whole.
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment: (s: string) => Iterable<{ segment: string }> } }).Segmenter;
  const g = Seg ? [...new Seg(undefined, { granularity: 'grapheme' }).segment(s)].map((x) => x.segment) : [...s];
  return g.filter((x) => /\p{Extended_Pictographic}/u.test(x));
}

export interface Draft {
  text: Record<TextKey, string>;
  emoji: Record<EmojiKey, string[]>;
  timezone: string;
}

export function draftOf(a: Account): Draft {
  const text = {} as Record<TextKey, string>;
  const emoji = {} as Record<EmojiKey, string[]>;
  for (const f of ALL) {
    if (f.emoji) emoji[f.key as EmojiKey] = parseEmojis(a[f.key] as string | null | undefined);
    else text[f.key as TextKey] = (a[f.key] as string | null | undefined) || '';
  }
  return { text, emoji, timezone: a.timezone || DEFAULT_TIMEZONE };
}

/** The account columns a draft maps to (empty text → null). */
export function payloadOf(d: Draft): Record<string, string | null> {
  const out: Record<string, string | null> = { timezone: d.timezone };
  for (const [k, v] of Object.entries(d.text)) out[k] = v.trim() || null;
  for (const [k, v] of Object.entries(d.emoji)) out[k] = serializeEmojis(v);
  return out;
}

export const isFilled = (d: Draft, f: FieldDef) =>
  f.emoji ? d.emoji[f.key as EmojiKey].length > 0 : d.text[f.key as TextKey].trim().length > 0;

/** The preset whose two chat styles match the draft exactly, if any. */
export function matchPreset(d: Draft): ChatStylePreset | undefined {
  const ns = d.text.conversational_dynamics.trim();
  const hs = d.text.hot_conversational_dynamics.trim();
  return CHAT_STYLE_PRESETS.find((p) => p.normal.trim() === ns && p.hot.trim() === hs);
}
